import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { removeOrderFromHistory, getOrdersByOrderNumber, nullifyOrder } from '../modules/orderdata';
import { getOrdersByOrderNumber as customerGetOrdersByOrderNumber } from '../modules/customerorderdata';
import { SPINNER_OFF, SPINNER_ON } from '../modules/appstate';
import ProductionOrderView from './productionorderview';
import { Button, Grid, CircularProgress, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import Footer from '../main/footer';
import FooterPadding from '../main/footerpadding';
import User from '../domain/user';
import { includes } from 'lodash';
import DeleteButton from '../common/deletebutton';
import { useTranslation } from 'react-i18next';
import DeleteFromHistoryConfirmationDialog from './deletefromhistoryconfirmationdialog';
import { getOrderPrintView, moveOrderToPortfolio } from '../modules/orderdata';
import { toast } from 'react-toastify';
import { ActionAllowed } from '../rbac/rbacutil';
import { rbacactions } from '../rbac/rbacrules';
import ChangePortfolioDialog from '../location/changeportfoliodialog';
import Logger from '../common/logger';
import ConfirmationDialog from '../common/confirmationdialog';
import { getOrderDisplayNumber } from '../domain/order';

export default function ProductionOrderViewContainer(props) {
    const dispatch = useDispatch();
    const customerUi = process.env.REACT_APP_TARGET === 'customerui';
    const ordersByOrderNumberCust = useSelector((state) => state.customerorderdata.ordersByOrderNumber);
    const ordersByOrderNumberVado = useSelector((state) => state.orderdata.ordersByOrderNumber);
    const userprofile = useSelector((state) => state.authentication.userProfile);
    const portfolios = useSelector((state) => state.portfoliodata.portfolioDescriptions);
    const [portfolio, setPortfolio] = useState({});
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [createPdfOnProgress, setCreatePdfOnProgress] = useState(false);
    const orderNumber = parseInt(props.match.params.orderNumber, 10);
    const orders = customerUi ? ordersByOrderNumberCust[orderNumber] : ordersByOrderNumberVado[orderNumber];
    const allowPricing = customerUi ? includes(userprofile.roles, User.RoleAllowPricing()) : true;
    const { t } = useTranslation();
    const [changePortfolioOpen, setChangePortfolioOpen] = useState(false);
    const [processingPortfolioChange, setProcessingPortfolioChange] = useState(false);
    const [nullifyConfirmOpen, setNullifyConfirmOpen] = useState(false);

    useEffect(() => {
        if (!orders) {
            if (customerUi) {
                dispatch(customerGetOrdersByOrderNumber(orderNumber));
            } else {
                dispatch(getOrdersByOrderNumber(orderNumber));
            }
        }
    }, [orders, customerUi, dispatch, orderNumber]);

    useEffect(() => {
        if (orders && portfolios && portfolios.length > 0) {
            setPortfolio(portfolios.find((p) => p.id === orders[0].portfolioId));
        }
    }, [orders, portfolios]);

    const close = () => {
        props.history.goBack();
    };

    const remove = () => {
        setRemoveDialogOpen(true);
    };

    const edit = () => {
        props.history.push(`/ordernumber/edit/${orders[0].orderNumber}`);
    };

    const cancelRemove = () => {
        setRemoveDialogOpen(false);
    };

    const confirmRemove = async (reason) => {
        setRemoving(true);
        setRemoveDialogOpen(false);
        if (await removeOrderFromHistory(orders[0].orderNumber, reason)(dispatch)) {
            close();
        }
        setRemoving(false);
    };

    const isRemovable = () => {
        return !!orders && orders[0].status === 4 && (!orders[0].invoiceNumbers || !orders[0].invoiceNumbers.length);
    };

    const isEditable = () => {
        return !!orders && orders[0].status === 4 && (!orders[0].invoiceNumbers || !orders[0].invoiceNumbers.length);
    };

    const onPrint = async () => {
        setCreatePdfOnProgress(true);
        await generatePdf();
    };

    const generatePdf = async () => {
        const displayNumber = orders && orders.length > 0 ? getOrderDisplayNumber(orders[0]) : orderNumber;
        const result = await getOrderPrintView(orderNumber, `${t('general.order')}_${displayNumber}.pdf`)(dispatch);

        if (result) {
            toast(t('createPdf.createPdfSuccess'), { timeout: 1000, hideProgressBar: true });
        } else {
            toast(t('createPdf.createPdfFail'), { autoClose: 5000 });
        }
        setCreatePdfOnProgress(false);
    };

    const changePortfolio = async (newPortfolioId) => {
        setProcessingPortfolioChange(true);
        var resp = await moveOrderToPortfolio(newPortfolioId, orders[0].orderNumber)(dispatch);
        if (resp) {
            toast.info(t('order.orderMovedToOtherPortfolio'), { autoClose: 2000 });
        } else {
            toast.error(t('order.errorInMovingToOtherPortfolio'), { autoClose: 5000 });
            Logger.LogError(`locationview.jsx; failed to change location portfolio to ${newPortfolioId}`, '');
        }
        setProcessingPortfolioChange(false);
        setChangePortfolioOpen(false);
    };

    const doNullifyOrder = async () => {
        dispatch({ type: SPINNER_ON });
        await nullifyOrder(orders[0].orderNumber)(dispatch);
        setNullifyConfirmOpen(false);
        dispatch({ type: SPINNER_OFF });
    };

    if (!orders) {
        return (
            <div>
                {t('general.loading')} <CircularProgress color={'secondary'} size={'1rem'} />
            </div>
        );
    }

    return (
        <Grid container>
            <ProductionOrderView
                orders={orders}
                close={close}
                allowPricing={allowPricing}
                portfolio={portfolio}
                customerUi={customerUi}
            />

            {removeDialogOpen && (
                <DeleteFromHistoryConfirmationDialog
                    open={removeDialogOpen}
                    cancel={cancelRemove}
                    confirm={confirmRemove}
                />
            )}
            <Footer>
                {!customerUi && (
                    <React.Fragment>
                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                name="btn-print"
                                onClick={onPrint}
                                startIcon={<PrintIcon />}
                                endIcon={
                                    createPdfOnProgress ? <CircularProgress color={'secondary'} size={'1rem'} /> : ''
                                }
                                disabled={createPdfOnProgress}>
                                {t('buttons.downloadPdf')}
                            </Button>
                        </Grid>

                        <Grid item>
                            <Tooltip title={isRemovable() ? '' : t('order.orderCannotBeRemoved')}>
                                <span>
                                    <DeleteButton
                                        onSubmit={remove}
                                        name="btn-remove"
                                        deleting={removing}
                                        disabled={!isRemovable() || removing}
                                    />
                                </span>
                            </Tooltip>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="contained"
                                color="secondary"
                                name="btn-edit"
                                onClick={edit}
                                disabled={!isEditable() || removing}
                                startIcon={<EditIcon />}
                                title={isEditable ? null : t('order.orderCannotBeEdited')}>
                                {t('buttons.fix')}
                            </Button>
                        </Grid>
                    </React.Fragment>
                )}
                {!customerUi && ActionAllowed(rbacactions.ChangeOrderPortfolio, userprofile) && (
                    <Grid item>
                        <Button
                            name="btn-change-portfolio"
                            variant="contained"
                            color="primary"
                            onClick={() => setChangePortfolioOpen(true)}
                            disabled={false}
                            startIcon={<SwapHorizIcon />}>
                            {t('general.portfolio')}
                        </Button>
                    </Grid>
                )}
                {!customerUi && ActionAllowed(rbacactions.NullifyOrderPrice, userprofile) && (
                    <Grid item>
                        <Button
                            name="btn-null-prices"
                            variant="contained"
                            color="primary"
                            onClick={() => setNullifyConfirmOpen(true)}
                            disabled={false}
                            startIcon={<MoneyOffIcon />}>
                            {t('order.nullifyPrices')}
                        </Button>
                    </Grid>
                )}
                <Grid item>
                    <Button disabled={removing} variant="contained" color="secondary" name="btn-close" onClick={close}>
                        {t('buttons.close')}
                    </Button>
                </Grid>
            </Footer>
            <FooterPadding />
            {changePortfolioOpen && (
                <ChangePortfolioDialog
                    open={changePortfolioOpen}
                    cancel={() => setChangePortfolioOpen(false)}
                    changePortfolio={changePortfolio}
                    currentPortfolioId={orders[0].portfolioId}
                    portfolios={portfolios}
                    infoText={t('general.portfolioChangeWithInvoices')}
                    processing={processingPortfolioChange}
                />
            )}
            <ConfirmationDialog
                open={nullifyConfirmOpen}
                confirm={doNullifyOrder}
                cancel={() => setNullifyConfirmOpen(false)}
                confirmText={t('order.nullifyPrices')}
                useDelay={true}>
                {t('order.confirmNullify')}
            </ConfirmationDialog>
        </Grid>
    );
}
